import { datadogLogs, type LogsEvent, type LogsEventDomainContext } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { RumEvent } from '@datadog/browser-rum-core';
import { useEffect } from 'react';

import { User } from '../../../lib/who-am-i/lib/user';

// Use git SHA for version to match Datadog source map uploads
// Falls back to 'local' for development builds without a commit SHA
const version = import.meta.env.REACT_APP_GIT_SHA || 'local';
export const RUM_SESSION_REPLAY_SAMPLE_RATE = 0;

type RumCspErrorEvent = RumEvent & {
	error?: {
		csp?: {
			disposition?: string;
		};
		source?: string;
	};
};

/**
 * Filters out low-signal RUM events:
 * - Browser-cancelled resource requests (status_code 0). Common causes:
 * - Network disconnect (user goes offline while requests are in-flight)
 * - Page navigation/reload (e.g. during deployments)
 * - Background tab throttling (Chrome kills idle tab connections after ~18s)
 * - React Query cancelling in-flight requests on component unmount
 * - Report-only CSP violations generated by browser reporting policies. These
 *   are observability noise, not blocked runtime errors.
 */
export const rumBeforeSend = (event: RumEvent) => {
	if (event.type === 'resource' && event.resource.status_code === 0) {
		return false;
	}

	if (event.type === 'error') {
		const error = (event as RumCspErrorEvent).error;

		if (error?.source === 'report' && error.csp?.disposition === 'report') {
			return false;
		}
	}

	return true;
};

/**
 * Filters out browser-logs network errors caused by browser-cancelled requests (status_code 0).
 * Common causes:
 * - Network disconnect (user goes offline while requests are in-flight)
 * - Page navigation/reload (e.g. during deployments)
 * - Background tab throttling (Chrome kills idle tab connections after ~18s)
 * - React Query cancelling in-flight requests on component unmount
 */
const logsBeforeSend = (event: LogsEvent, context: LogsEventDomainContext) => {
	if (event.origin === 'network' && event.http?.status_code === 0) {
		return false;
	}
	if (event.origin === 'network' && 'isAborted' in context && context.isAborted) {
		return false;
	}
	return true;
};

interface TelemetryParams {
	applicationId: string;
	clientToken: string;
	service: string;
	env: string;
	user?: User;
}

export const useInitTelemetry = ({ applicationId, clientToken, service, env, user }: TelemetryParams) => {
	useEffect(() => {
		// Temporary until we have a way to test in development
		if (['production', 'staging'].includes(env)) {
			// Guard against double initialization (React StrictMode calls effects twice)
			if (!datadogRum.getInternalContext()) {
				datadogRum.init({
					applicationId: applicationId,
					clientToken: clientToken,
					site: 'datadoghq.com',
					service: service,
					env: env,
					version: version,
					sessionSampleRate: 100,
					sessionReplaySampleRate: RUM_SESSION_REPLAY_SAMPLE_RATE,
					trackUserInteractions: true,
					trackResources: true,
					trackLongTasks: true,
					defaultPrivacyLevel: 'mask-user-input',
					beforeSend: rumBeforeSend,
				});
			}

			if (!datadogLogs.getInternalContext()) {
				datadogLogs.init({
					clientToken: clientToken,
					site: 'datadoghq.com',
					service: service,
					env: env,
					version: version,
					forwardErrorsToLogs: true,
					sessionSampleRate: 100,
					beforeSend: logsBeforeSend,
				});
			}
		}
	}, [applicationId, clientToken, service, env]);

	// Set user context when user data is available
	useEffect(() => {
		if (user) {
			const userContext = {
				id: user.id,
				email: user.email,
			};

			// Set user context for RUM
			if (window.DD_RUM) {
				window.DD_RUM.setUser(userContext);
			}

			// Set user context for Logs
			datadogLogs.setUserProperty('id', user.id);
			datadogLogs.setUserProperty('email', user.email);
		}
	}, [user]);
};
